/* mg-storefront-track.js
 *
 * First-party event transport for motiongrey.com -> the employee portal.
 *
 * Exposes one global, window.mgTrack(name, payload), which queues an event and
 * gets it delivered. Everything hard about doing that reliably lives here so
 * callers stay one line long.
 *
 * WHY THIS EXISTS AT ALL
 * GA4 and Clarity already receive these events, and both stay. But both are
 * third-party and both are blocked by ad-blocking software for a share of
 * shoppers. A first-party endpoint on our own domain is not blocked, is never
 * sampled, and the raw rows stay ours. The two run side by side deliberately:
 * if our numbers and Clarity's stop agreeing, that disagreement is the alarm.
 *
 * THE FOUR THINGS THAT MAKE CLICK TRACKING WRONG, AND WHAT IS DONE ABOUT THEM
 *
 * 1. Events lost to navigation. A click on a product card starts loading the
 *    next page, and the browser cancels any request still in flight -- so the
 *    most valuable events are exactly the ones you lose. Everything here goes
 *    out via navigator.sendBeacon, which the browser commits to delivering even
 *    while the page is being torn down, and the queue is force-flushed on
 *    pagehide. Callers that navigate can also demand an immediate flush.
 *
 * 2. Duplicates. sendBeacon can deliver twice, and a shopper can reload
 *    mid-flight. Every event carries an eventKey unique to (page view, name,
 *    sequence); the server discards repeats, so a replay is a no-op rather than
 *    a double count. Retrying is therefore always safe.
 *
 * 3. Wasted requests. One HTTP call per event does not survive contact with
 *    listing impressions, where a single collection page can produce twenty.
 *    Events are batched and flushed together.
 *
 * 4. Silent failure. If this file throws, it must not take the storefront with
 *    it -- analytics is never worth breaking a product page for. Every entry
 *    point is wrapped, and any failure is swallowed.
 *
 * PRIVACY
 * No personal data is collected here: no name, email, customer id, cart
 * contents or order value. visitorKey is a random string in localStorage whose
 * only job is to separate "three clicks by one person" from "three people". It
 * is not linked to any account and carries no meaning off this site.
 */
(function () {
  'use strict';

  // window.mgTrack may already exist as the inline stub that buffers early
  // calls; only bail if a real transport is already installed.
  if (typeof window === 'undefined') return;
  if (window.mgTrack && window.mgTrack.__mgReal) return;

  var cfg = window.mgTrackConfig || {};
  var endpoint = typeof cfg.endpoint === 'string' ? cfg.endpoint.trim() : '';
  var enabled = cfg.enabled === true && endpoint !== '';

  var VISITOR_STORAGE_KEY = 'mg_visitor_key';
  var MAX_QUEUE = 50;      // matches the server's per-request batch cap
  var FLUSH_DELAY_MS = 800; // long enough to batch a burst, short enough to survive a quick exit

  var queue = [];
  var timer = null;
  var sequence = 0;

  function randomKey() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      if (window.crypto && window.crypto.getRandomValues) {
        var bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        return Array.prototype.map.call(bytes, function (b) {
          return ('0' + b.toString(16)).slice(-2);
        }).join('');
      }
    } catch (e) {}
    return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  // Persists across visits so repeat shoppers are not counted as new people.
  // Storage can throw in private mode or when disabled, in which case we fall
  // back to a per-page-view identity rather than giving up on the event.
  var visitorKey = (function () {
    try {
      var existing = window.localStorage.getItem(VISITOR_STORAGE_KEY);
      if (existing) return existing;
      var fresh = randomKey();
      window.localStorage.setItem(VISITOR_STORAGE_KEY, fresh);
      return fresh;
    } catch (e) {
      return null;
    }
  })();

  var pageViewKey = randomKey();

  /**
   * Shopify's own long-lived visitor id, from the _shopify_y cookie.
   *
   * This is the join key to revenue, and it is the whole reason a purchase can
   * ever be attributed back to an interaction. Shopify stamps the same value on
   * the checkout_completed event its Web Pixels emit, so a custom pixel posting
   * completed orders to our endpoint lands rows that match these ones.
   *
   * It has to come from the cookie rather than our own visitorKey because a
   * custom pixel runs in a sandbox with no access to this page's localStorage —
   * Shopify's id is the only identifier both sides can see.
   */
  function shopifyClientId() {
    try {
      var match = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
      if (match) return decodeURIComponent(match[1]).slice(0, 60);
      var user = window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.user;
      var traits = user ? user().traits() : null;
      if (traits && traits.uniqToken) return String(traits.uniqToken).slice(0, 60);
    } catch (e) {}
    return null;
  }

  function post(batch) {
    if (!batch.length) return;
    var body = JSON.stringify({ events: batch });
    try {
      // text/plain keeps this a "simple" cross-origin request, so the browser
      // skips the CORS preflight -- a preflight cannot be relied on during
      // pagehide, which is exactly when clicks are delivered.
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(endpoint, blob)) return;
      }
      // keepalive lets the request outlive the page, same as a beacon.
      fetch(endpoint, {
        method: 'POST',
        body: body,
        keepalive: true,
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' }
      }).catch(function () {});
    } catch (e) {}
  }

  function flush() {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!queue.length) return;
    var batch = queue.splice(0, queue.length);
    post(batch);
  }

  function schedule() {
    if (timer) return;
    timer = setTimeout(flush, FLUSH_DELAY_MS);
  }

  /**
   * Queue an event.
   *
   * @param {string} name    one of the names the server accepts; anything else
   *                         is dropped server-side rather than stored
   * @param {object} payload event fields
   * @param {object} [opts]  { immediate: true } to flush now, for anything that
   *                         is about to navigate away
   */
  function mgTrack(name, payload, opts) {
    try {
      if (!enabled || typeof name !== 'string' || !name) return;

      sequence += 1;
      var event = {
        eventKey: pageViewKey + '.' + name + '.' + sequence,
        eventName: name,
        clientTs: new Date().toISOString(),
        visitorKey: visitorKey,
        pageViewKey: pageViewKey,
        // read per event rather than once at load: the cookie is set by
        // Shopify's own scripts and may not exist yet on a first-ever visit
        shopifyClientId: shopifyClientId(),
        pagePath: window.location.pathname
      };
      if (payload && typeof payload === 'object') {
        for (var key in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== undefined) {
            event[key] = payload[key];
          }
        }
      }

      queue.push(event);
      if (opts && opts.immediate) flush();
      else if (queue.length >= MAX_QUEUE) flush();
      else schedule();
    } catch (e) {}
  }

  // pagehide is the reliable one: on mobile Safari a backgrounded tab may never
  // fire unload, and visibilitychange covers the tab-switch case. Both are
  // cheap, and flush() is a no-op on an empty queue.
  try {
    window.addEventListener('pagehide', flush, { capture: true });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush();
    });
  } catch (e) {}

  mgTrack.__mgReal = true;
  mgTrack.flush = flush;
  mgTrack.enabled = enabled;
  window.mgTrack = mgTrack;

  // Drain anything the stub buffered before this file arrived.
  try {
    var pending = window.mgTrackQueue || [];
    window.mgTrackQueue = [];
    for (var i = 0; i < pending.length; i++) {
      mgTrack.apply(null, pending[i]);
    }
  } catch (e) {}
})();
